//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { apis } from 'utils/lib/apis';
import { getAwsAuthToken } from 'utils/lib/authHelper';
import sysConf from 'config';
import { getStateNameFromCode } from 'utils/lib/standardHelper';

//  -- Actions ------------------------
import * as Actions from './actions';
import { logToSentry } from "utils/networkErrorUtils";
import { has } from "lodash"
//  ----------------------------------------------------------------------------
//  Thunks
//  ----------------------------------------------------------------------------

//  -- Create log ----------------
export const handleListStory = () => async (dispatch) => {
	try {
		const stories = await apis.get('story', `/stories`)
		if (Array.isArray(stories)) {
			dispatch(Actions.listStory(stories))
			return stories;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'listing stories',
                error,
                module: 'STORY',
                operation: 'LIST_STORY'
            });
		}
	}
};

export const handleListStoryStandard = (userId,role,orgId) => async (dispatch) => {
	try {
		const standards = await apis.get('story', `/standards?userId=${userId}&role=${role}&orgId=${orgId}`, {
			userId,
			role,
			orgId
		})
		if (Array.isArray(standards)) {
			dispatch(Actions.setStandards(standards))
			return standards;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'listing standards',
                error,
                module: 'STORY',
                operation: 'LIST_STORY'
            });
		}
	}
};
export const handleUpdateStanders = (data) => async (dispatch) => {
	try {
		const standards = await apis.put('story', `/standards`,{
			body:data
		})
		if (standards) {
			dispatch(Actions.setUpdatedStandards(standards))
			// Refresh the standards list to show the updated standard
			await dispatch(handleListStoryStandard());
			return standards;
		}
		return standards;
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'updating standards',
                error,
                module: 'STORY',
                operation: 'UPDATE_STORY'
            });
		}
	}
};
export const handleCreateStanders = (data) => async (dispatch) => {
	try {
		const standards = await apis.post('story', `/standards`,{
			body:data
		})
		if (standards) {
			dispatch(Actions.setNewStandards(standards))
			// Refresh the standards list to show the newly created standard
			await dispatch(handleListStoryStandard());
			return standards;
		}
		return standards;
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'creating standards',
                error,
                module: 'STORY',
                operation: 'CREATE_STORY'
            });
		}
	}
};
export const handleGenerateStateStandards = (defaultStandard) => async (dispatch) => {
	try {
		const result = await apis.post('story', `/standards/generate`, {
			body: {
				abbr: defaultStandard.abbr,
				description: defaultStandard.description,
				grade_level: defaultStandard.grade_level
			}
		});
		if (Array.isArray(result)) {
			return result;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
				message: 'generating state standards',
				error,
				module: 'STORY',
				operation: 'GENERATE_STATE_STANDARDS'
			});
		}
		throw error;
	}
};

export const handleApproveStandard = (id, status) => async (dispatch) => {
	try {
		const result = await apis.post('story', `/standards/approve`, {
			body: {
				id: id,
				status: status
			}
		});
		if (result && result.success === true) {
			dispatch(handleListStoryStandard());
			return result;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
				message: 'approving standard',
				error,
				module: 'STORY',
				operation: 'APPROVE_STANDARD'
			});
		}
		throw error;
	}
};

const convertStandardStateCodeToName = (standard) => {
	if (!standard || !standard.state) return standard;
	let stateName = getStateNameFromCode(standard.state) || standard.state;
	
	// Convert Common Core to COMMONCORE, otherwise uppercase and trim
	if (stateName && stateName.toUpperCase().trim() === 'COMMON CORE') {
		stateName = 'COMMONCORE';
	} else if (stateName && stateName !== 'COMMONCORE') {
		stateName = stateName.toUpperCase().trim();
	}
	
	return {
		...standard,
		state: stateName
	};
};

export const handleAiGenerateStandards = (data, onStreamUpdate, onStatusUpdate) => async (dispatch) => {
	try {
		const prodUrl = `${sysConf.apiGateway.AI_BASE_NODE_URL}/ai/generate-standards`;
		const token = await getAwsAuthToken();
		const response = await fetch(prodUrl, {
			method: 'POST',
			headers: { 
				'Content-Type': 'application/json',
				"x-auth-source": sysConf.isDev ? "test" : "prod",
				'Authorization': `Bearer ${token}`,
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
		}

		const contentType = response.headers.get('content-type') || '';
		const isStreaming = contentType.includes('text/event-stream') || contentType.includes('text/plain');

		if (isStreaming && onStreamUpdate && response.body) {
			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';
			const standards = [];
			let currentEvent = null;
			let accumulatingData = null;
			let braceCount = 0;

			try {
				while (true) {
					const { done, value } = await reader.read();
					
					if (done) {
						if (accumulatingData) {
							const completeData = accumulatingData.trim();
							if (completeData && completeData !== '[DONE]') {
								try {
									const standardData = JSON.parse(completeData);
									if (standardData && standardData.abbr) {
										const convertedStandard = convertStandardStateCodeToName(standardData);
										standards.push(convertedStandard);
										if (onStreamUpdate) {
											onStreamUpdate(convertedStandard, standards);
										}
									}
								} catch (parseError) {
									console.warn('Failed to parse final accumulated data:', completeData, parseError);
								}
							}
						}
						if (buffer.trim()) {
							const lines = buffer.split('\n');
							let prevEvent = null;
							for (let i = 0; i < lines.length; i++) {
								const line = lines[i];
								if (line.startsWith('event: ')) {
									prevEvent = line.slice(7).trim();
									continue;
								}
								if (line.startsWith('data: ')) {
									const dataStr = line.slice(6).trim();
									if (dataStr === '[DONE]') {
										return standards;
									}
									if (prevEvent === 'status') {
										try {
											const statusData = JSON.parse(dataStr);
											if (onStatusUpdate && statusData.message) {
												onStatusUpdate(statusData.message);
											}
										} catch (parseError) {
											console.warn('Failed to parse status data:', dataStr, parseError);
										}
										prevEvent = null;
										continue;
									}
									try {
										const standardData = JSON.parse(dataStr);
										if (standardData && standardData.abbr) {
											const convertedStandard = convertStandardStateCodeToName(standardData);
											standards.push(convertedStandard);
											if (onStreamUpdate) {
												onStreamUpdate(convertedStandard, standards);
											}
										}
									} catch (parseError) {
										if (dataStr !== '[DONE]') {
											console.warn('Failed to parse final buffer:', dataStr, parseError);
										}
									}
									prevEvent = null;
								}
							}
						}
						break;
					}

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split('\n');
					buffer = lines.pop() || '';

					for (let i = 0; i < lines.length; i++) {
						const line = lines[i];
						
						if (line.trim() === '') {
							if (accumulatingData && braceCount === 0) {
								const completeData = accumulatingData.trim();
								if (completeData && completeData !== '[DONE]') {
									try {
										const standardData = JSON.parse(completeData);
										if (standardData && standardData.abbr) {
											const convertedStandard = convertStandardStateCodeToName(standardData);
											standards.push(convertedStandard);
											if (onStreamUpdate) {
												onStreamUpdate(convertedStandard, standards);
											}
										}
									} catch (parseError) {
										if (completeData !== '[DONE]') {
											console.warn('Failed to parse SSE data:', completeData, parseError);
										}
									}
								}
								accumulatingData = null;
								braceCount = 0;
							}
							if (currentEvent === 'error' && i > 0) {
								const prevLine = lines[i - 1];
								if (prevLine.startsWith('data: ')) {
									try {
										const errorData = JSON.parse(prevLine.slice(6).trim());
										throw new Error(errorData.message || errorData.error || 'Stream error occurred');
									} catch (parseError) {
										if (parseError instanceof Error && parseError.message.includes('Stream error')) {
											throw parseError;
										}
										throw new Error('Error occurred during streaming');
									}
								}
							}
							currentEvent = null;
							continue;
						}

						if (line.startsWith('event: ')) {
							currentEvent = line.slice(7).trim();
							if (currentEvent === 'done') {
								return standards;
							}
							continue;
						}

						if (currentEvent === 'status' && line.startsWith('data: ')) {
							const dataStr = line.slice(6).trim();
							try {
								const statusData = JSON.parse(dataStr);
								if (onStatusUpdate && statusData.message) {
									onStatusUpdate(statusData.message);
								}
							} catch (parseError) {
								if (dataStr !== '[DONE]') {
									console.warn('Failed to parse status data:', dataStr, parseError);
								}
							}
							currentEvent = null;
							continue;
						}

						if (line.startsWith('data: ')) {
							const dataStr = line.slice(6);
							
							if (dataStr.trim() === '[DONE]') {
								return standards;
							}

							if (currentEvent === 'error') {
								try {
									const errorData = JSON.parse(dataStr.trim());
									throw new Error(errorData.message || errorData.error || 'Stream error occurred');
								} catch (parseError) {
									if (parseError instanceof Error && parseError.message.includes('Stream error')) {
										throw parseError;
									}
									throw new Error('Error occurred during streaming');
								}
							}

							if (currentEvent === 'status') {
								try {
									const statusData = JSON.parse(dataStr.trim());
									if (onStatusUpdate && statusData.message) {
										onStatusUpdate(statusData.message);
									}
								} catch (parseError) {
									console.warn('Failed to parse status data:', dataStr, parseError);
								}
								currentEvent = null;
								continue;
							}

							if (accumulatingData === null) {
								accumulatingData = dataStr;
								braceCount = (dataStr.match(/\{/g) || []).length - (dataStr.match(/\}/g) || []).length;
							} else {
								accumulatingData += '\n' + dataStr;
								braceCount += (dataStr.match(/\{/g) || []).length - (dataStr.match(/\}/g) || []).length;
							}

							if (braceCount === 0 && accumulatingData) {
								const completeData = accumulatingData.trim();
								if (completeData && completeData !== '[DONE]') {
									try {
										const standardData = JSON.parse(completeData);
										if (standardData && standardData.abbr) {
											const convertedStandard = convertStandardStateCodeToName(standardData);
											standards.push(convertedStandard);
											if (onStreamUpdate) {
												onStreamUpdate(convertedStandard, standards);
											}
										}
									} catch (parseError) {
										if (completeData !== '[DONE]') {
											console.warn('Failed to parse SSE data:', completeData, parseError);
										}
									}
								}
								accumulatingData = null;
								braceCount = 0;
							}
							currentEvent = null;
						} else if (accumulatingData !== null) {
							accumulatingData += '\n' + line;
							braceCount += (line.match(/\{/g) || []).length - (line.match(/\}/g) || []).length;
							
							if (braceCount === 0) {
								const completeData = accumulatingData.trim();
								if (completeData && completeData !== '[DONE]') {
									try {
										const standardData = JSON.parse(completeData);
										if (standardData && standardData.abbr) {
											const convertedStandard = convertStandardStateCodeToName(standardData);
											standards.push(convertedStandard);
											if (onStreamUpdate) {
												onStreamUpdate(convertedStandard, standards);
											}
										}
									} catch (parseError) {
										if (completeData !== '[DONE]') {
											console.warn('Failed to parse SSE data:', completeData, parseError);
										}
									}
								}
								accumulatingData = null;
								braceCount = 0;
							}
						}
					}
				}

				return standards.map(standard => convertStandardStateCodeToName(standard));
			} finally {
				reader.releaseLock();
			}
		}

		const result = await response.json();
		
		if (!result) {
			throw new Error('No response from server');
		}
		if (result.success === false) {
			throw new Error(result.message || 'Failed to generate standards');
		}
		
		let standardsArray = [];
		if (result && Array.isArray(result)) {
			standardsArray = result;
		} else if (result && result.data && Array.isArray(result.data)) {
			standardsArray = result.data;
		} else if (result && result.success === true && result.data && Array.isArray(result.data)) {
			standardsArray = result.data;
		}
		
		return standardsArray.map(standard => convertStandardStateCodeToName(standard));
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
				message: 'AI generating standards',
				error,
				module: 'STORY',
				operation: 'AI_GENERATE_STANDARDS'
			});
		}
		throw error;
	}
};

export const handleBulkCreateStandards = (standards) => async (dispatch) => {
	try {
		const result = await apis.post('story', `/standards/bulk-create`, {
			body: { standards }
		});
		if (result) {
			await dispatch(handleListStoryStandard());
			return result;
		}
		return result;
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
				message: 'bulk creating standards',
				error,
				module: 'STORY',
				operation: 'BULK_CREATE_STANDARDS'
			});
		}
		throw error;
	}
};

export const handleDeleteStanders = (id) => async (dispatch) => {
	try {
		const standards = await apis.del('story', `/standards/${id}`)
		if (standards) {
			dispatch(Actions.setDeleteStandards(standards))
			// Refresh the standards list to remove the deleted standard
			await dispatch(handleListStoryStandard());
			return standards;
		}
		return standards;
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'deleting standards',
                error,
                module: 'STORY',
                operation: 'DELETE_STORY'
            });
		}
	}
};
export const handleCreateStories = (data) => async (dispatch) => {
	try {
		const stories = await apis.post('story', `/story`,{
			body:data
		})
		if (has(stories,"id")) {
			dispatch(Actions.createStory(stories))
			return stories;
		}
		return null;;
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'creating stories',
                error,
                module: 'STORY',
                operation: 'CREATE_STORY'
            });
		}
	}

}

export const handleUpdateStories = (id,data) => async (dispatch) => {
	try {
		const stories = await apis.put('story', `/story/${id}`,{
			body:data
		})
		if (has(stories,"id")) {
			dispatch(Actions.updateStory(stories))
			return stories;
		}
		return null;;
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'updating stories',
                error,
                module: 'STORY',
                operation: 'UPDATE_STORY'
            });
		}
	}

};
export const handleDeleteStories = (id) => async (dispatch) => {
	try {
		await apis.del('story', `/story/${id}`)
		dispatch(Actions.deleteStory(id))
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'deleting stories',
                error,
                module: 'STORY',
                operation: 'DELETE_STORY'
            });
		}
	}	
};
export const handleListOrganizationLitracyOverall = (orgId) => async (dispatch) => {
	try {
		const overAllData = await apis.get('story', `/listDistrictOverAll/${orgId}`)
		dispatch(Actions.setOverallLiteracyPurpose(overAllData))
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'listing organization literacy overall',
                error,
                module: 'STORY',
                operation: 'LIST_ORGANIZATION_LESSON_ORGANIZATION'
            });
		}
	}
};
export const handleListSchoolsLitracy = (orgId) => async (dispatch) => {
	try {
		const overAllData = await apis.get('story', `/listBySchools/${orgId}`)
		if(Array.isArray(overAllData)){
			return overAllData;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'listing schools literacy',
                error,
                module: 'STORY',
                operation: 'LIST_SUPER_LESSON'
            });
		}
		return [];
	}
};

export const handleListTeacherLitracy = (userId) => async (dispatch) => {
	try {
		const overAllData = await apis.get('story', `/listByTeacher/${userId}`)
		if(Array.isArray(overAllData)){
			return overAllData;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'listing teacher literacy',
                error,
                module: 'STORY',
                operation: 'LIST_TEACHER_LESSON'
            });
		}
		return [];
	}
};
export const handleListStudentLitracy = (userId) => async (dispatch) => {
	try {
		const overAllData = await apis.get('story', `/listByStudent/${userId}`)
		if(Array.isArray(overAllData)){
			return overAllData;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
                message: 'listing student literacy',
                error,
                module: 'STORY',
                operation: 'LIST_SUPER_LESSON'
            });
		}
		return [];
	}
};

export const handleAutoSelectStandards = (data) => async (dispatch) => {
	try {
		const payload = {
			questions: data.questions,
			bookId: data.bookId,
			fromPage: data.fromPage,
			toPage: data.toPage
		};
		
		// Add grade if provided
		if (data.grade !== null && data.grade !== undefined) {
			payload.grade = data.grade;
		}
		
		// Add state if provided
		if (data.state !== null && data.state !== undefined) {
			payload.state = data.state;
		}
		
		const result = await apis.post('story', `/standards/auto-select`, {
			body: payload
		});
		if (result && result.success && Array.isArray(result.results)) {
			return result.results;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
				message: 'auto selecting standards',
				error,
				module: 'STORY',
				operation: 'AUTO_SELECT_STANDARDS'
			});
		}
		throw error;
	}
};

export const handleAutoSelectStandardsForAllQuestions = (data) => async (dispatch) => {
	try {
		const payload = {
			questions: data.questions,
			bookId: data.bookId,
			pageId: data.pageId,
			gradeLevels: data.gradeLevels
		};
		
		const result = await apis.post('story', `/standards/auto-select-all`, {
			body: payload
		});
		if (result && result.success && Array.isArray(result.results)) {
			return result.results;
		}
		return [];
	} catch (error) {
		if (error && error.message != "Network Error") {
			logToSentry({
				message: 'auto selecting standards for all questions',
				error,
				module: 'STORY',
				operation: 'AUTO_SELECT_STANDARDS_FOR_ALL'
			});
		}
		throw error;
	}
};